import { Box, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PropTypes from 'prop-types';
import {
  bodyFont, CARD_RADIUS, homeColors, homeEase, PRODUCT_CARD_HEIGHT, shadowSoft, shadowSoftStrong,
  titleFont,
} from '../style/homeStyles';

const ICON_SIZE = 92;
const ICON_SIZE_DESKTOP = 120;

// Cara del ícono que gira: al hacer hover se da vuelta y muestra el CTA.
const faceSx = {
  position: 'absolute',
  inset: 0,
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '20px',
  overflow: 'hidden',
};

const ProductCard = ({ product, isMobile }) => {
  const isPink = product.variant === 'pink';
  const iconSize = isMobile ? ICON_SIZE : ICON_SIZE_DESKTOP;

  return (
    <Box
      component="article"
      sx={{
        position: 'relative',
        borderRadius: CARD_RADIUS,
        padding: isMobile ? '20px 20px' : '32px',
        // En desktop las dos tarjetas conviven en una fila y tienen que medir lo mismo.
        height: '100%',
        minHeight: isMobile ? 0 : PRODUCT_CARD_HEIGHT,
        overflow: 'hidden',
        background: isPink
          ? 'linear-gradient(155deg, #FF1F7E 0%, #DA0067 100%)'
          : homeColors.white,
        color: isPink ? homeColors.white : homeColors.ink,
        // La sombra reforzada es parte del rediseño desktop; en mobile queda la original.
        boxShadow: isMobile ? shadowSoft : shadowSoftStrong,
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: isMobile ? '16px' : '28px', height: '100%',
      }}
      >
        <Box
          component="a"
          href={product.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={product.ctaText}
          sx={{
            width: iconSize,
            height: iconSize,
            flexShrink: 0,
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            perspective: '900px',
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'inherit',
            backgroundColor: isPink ? 'rgba(255,255,255,0.14)' : 'transparent',
            boxShadow: isPink
              ? 'inset 0 0 0 1px rgba(255,255,255,0.18), 0 6px 16px -8px rgba(0,0,0,0.25)'
              : '0 6px 16px -8px rgba(31,27,29,0.18)',
            backdropFilter: isPink ? 'blur(6px)' : 'none',
            WebkitBackdropFilter: isPink ? 'blur(6px)' : 'none',
            transition: `transform .35s ${homeEase}`,
            animation: 'homeProductIdle 5s ease-in-out infinite',
            '&:hover': { animationPlayState: 'paused' },
            '&:active': { transform: 'scale(0.97)' },
            '&:hover .home-product-flipper, &:focus-visible .home-product-flipper': {
              transform: 'rotateY(180deg)',
            },
            '@keyframes homeProductIdle': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-3px)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              '& .home-product-flipper': { transitionDuration: '0s' },
            },
          }}
        >
          <Box
            className="home-product-flipper"
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'block',
              transformStyle: 'preserve-3d',
              transition: `transform .65s ${homeEase}`,
              borderRadius: 'inherit',
            }}
          >
            <Box sx={faceSx}>
              <Box
                component="img"
                src={product.image}
                alt=""
                sx={{
                  width: '108%', height: '108%', objectFit: 'contain', display: 'block',
                }}
              />
            </Box>
            <Box
              sx={{
                ...faceSx,
                transform: 'rotateY(180deg)',
                flexDirection: 'column',
                gap: '6px',
                padding: '6px',
                backgroundColor: isPink ? homeColors.white : homeColors.pink,
                color: isPink ? homeColors.pink : homeColors.white,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: titleFont,
                  fontWeight: 600,
                  fontSize: 12.5,
                  lineHeight: 1.15,
                  letterSpacing: '-0.01em',
                  textAlign: 'center',
                  padding: '0 4px',
                }}
              >
                {product.ctaText}
              </Typography>
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <Typography
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 22,
              padding: '0 10px',
              borderRadius: '999px',
              backgroundColor: isPink ? 'rgba(255,255,255,0.18)' : homeColors.pink50,
              color: isPink ? homeColors.white : homeColors.pink,
              fontFamily: bodyFont,
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: '0.02em',
            }}
          >
            {product.tag}
          </Typography>
          <Typography
            component="h3"
            sx={{
              margin: '10px 0 6px',
              fontFamily: titleFont,
              fontWeight: 700,
              fontSize: isMobile ? 22 : 28,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              color: 'inherit',
            }}
          >
            {product.title}
          </Typography>
          <Typography
            component="p"
            sx={{
              margin: 0,
              fontFamily: bodyFont,
              fontSize: isMobile ? 14 : 16,
              lineHeight: 1.5,
              color: isPink ? 'rgba(255,255,255,0.82)' : homeColors.ink2,
            }}
          >
            {product.description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

ProductCard.propTypes = {
  product: PropTypes.shape({
    title: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    ctaText: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['default', 'pink']),
  }).isRequired,
  isMobile: PropTypes.bool,
};

ProductCard.defaultProps = {
  isMobile: false,
};

export default ProductCard;
